import { useKiosk } from "./useKiosk";
import { isMobilePhone } from "./phoneSetupPolicy";

/**
 * 結帳簡訊驗證的對象（2026-10-14 使用者拍板，Kiosk 與瓦斯訂購統一）：
 * 一律是「帳號手機」，不是這張單的聯絡電話。結算頁 OtpCard 用這裡決定要寄去哪支。
 * 帳號沒有手機時回空字串：由 OtpCard 的 noAccountPhone 輸入模式處理，不可退回聯絡電話。
 */
export const checkoutOtpPhone = (s: { accountPhone?: string | null; contactPhone?: string | null }): string =>
  (s.accountPhone || "").replace(/\D/g, "");

/**
 * 改這張單的聯絡電話：只影響這張單——不作廢驗證、不重寄簡訊、不改帳號手機。
 * （舊做法在換號時清掉 otpVerified/phoneVerifiedTicket，是因為以前驗的就是聯絡電話；
 * 現在驗證對象固定是帳號手機，聯絡電話與驗證無關。）
 */
export const saveContactPhoneOnly = (phone: string): void => {
  useKiosk.getState().setContactPhone(phone);
};

/**
 * 驗證卡一進來該做什麼：
 *  - "send"：號碼是手機 → 顯示確認段，客戶勾協議、按「傳送驗證碼」才寄。
 *  - "edit"：帳號資料已載入，但號碼不是手機（市話、或像 'XXXXCUS_1234' 這種非數字的匯入垃圾）
 *            → 不可以寄（寄不到、還會吃掉每帳號 60 秒冷卻），直接打開「修改號碼」讓使用者輸入手機。
 *  - "wait"：帳號資料還沒回來（號碼是空的）→ 什麼都不做，等資料。
 * accountLoaded 沒給時，以「有沒有號碼」代替（舊呼叫端行為不變）。
 */
export type CheckoutOtpCardMode = "send" | "edit" | "wait";
export const checkoutOtpCardMode = (s: { phone?: string | null; accountLoaded?: boolean }): CheckoutOtpCardMode => {
  if (isMobilePhone(s.phone)) return "send";
  const loaded = s.accountLoaded ?? !!(s.phone || "").trim();
  return loaded ? "edit" : "wait";
};

/**
 * 瓦斯訂購結算頁要不要掛驗證卡。
 * 以前用「otpPhone 非空」當條件：帳號手機是 'CUS_' 這類全非數字的值時 otpPhone 會是空字串，
 * 補登區塊又不出現（帳號「有」號碼），結果卡不顯示、送出又被擋 → 走不下去。
 * 現在改看「帳號資料已載入」：載入後只要需要驗證、或帳號沒手機（showPhoneSetup，卡片開輸入模式），就一定掛卡
 *（號碼不是手機時卡會直接開在「修改號碼」）。
 */
export const shouldMountCheckoutOtpCard = (s: { showPhoneSetup: boolean; needOtpCard: boolean; accountLoaded: boolean }): boolean =>
  s.accountLoaded && (s.showPhoneSetup || s.needOtpCard);
